import React, { FC, useContext } from 'react';

import { useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Card,
  CardAccompanyingInfo,
  CardDescription,
  CardDurationInfo,
  CardGragient,
  CardHeader,
  CardImageLabel,
  CardImg,
  CardInfo,
  CardLabel,
  CardLabels,
  CardPlayIcon,
  CardProgressLabel,
  CardRepeatIcon,
} from '../../../../components/Card';
import CardPathwayActionBlock from '../../../../components/Card/components/CardPathwayActionBlock/CardPathwayActionBlock';
import Ratio from '../../../../components/Ratio';
import ToggleFromList from '../../../../components/ToggleFromList/ToggleFromList';
import { CategoryLabel } from '../../../../components/UI/CategoryLabel';
import CategoryLabelWrapper from '../../../../components/UI/CategoryLabel/CategoryLabelWrapper';
import DifficultyIcon from '../../../../components/UI/DifficultyIcon';
import { ProgressLine } from '../../../../components/UI/ProgressLine';
import { DEFAULT_DISCOVER_URL } from '../../../../const';
import { UserInfoCtx } from '../../../../containers/CommonLayout/CommonLayout';
import useToggleFromWatchlist from '../../../../hooks/useToggleFromWatchlist';
import { DiscoverRoutes } from '../../../../routes';
import styled, { css } from '../../../../styled';
import { MIDDLE_MARGIN_PX, SMALL_MARGIN_PX } from '../../../../theme';
import { DashVideo, DiscoverPathwaysDetailedInfo } from '../../../../types';
import { WIDE_CATEGORY_LABEL_WIDTH } from '../../../../utils/const';
import { formatDateToNewsFormat } from '../../../../utils/dateUtils/formatDateToNewsFormat';
import { secondsToHHHmmm } from '../../../../utils/dateUtils/secondsToHHHmmm';
import { secondsToHms } from '../../../../utils/dateUtils/secondsToHms';
import { createCategoryLink } from '../../../../utils/urlUtils/createCategoryLink';
import { calcVideoProgressAndLinkToShow } from '../../../../utils/videoProgressUtils';
import { HITS_TYPES } from '../../../const';
import { SendHitsEvent } from '../../const';
import { sendAlgoliaClickEvent } from '../../helpers';
import { GlossaryHit } from '../GlossaryHit';

const getCategoriesArray = (item, isGridHit = false) => {
  const res: React.ReactNode[] = [];

  if (item.searchable_type === HITS_TYPES.VIDEO || item.searchable_type === HITS_TYPES.PATHWAY) {
    if (item?.categories_array) {
      const filteredCategoriesArr = item.categories_array.filter((item) => item?.id && item?.title);

      if (isGridHit) {
        const categoryLink = createCategoryLink(filteredCategoriesArr?.[0]);
        filteredCategoriesArr.length &&
          res.push(
            <CategoryLabel
              key={filteredCategoriesArr[0]?.id}
              name={filteredCategoriesArr[0]?.title}
              textMaxWidth={WIDE_CATEGORY_LABEL_WIDTH}
              url={categoryLink}
            />,
          );
      } else {
        filteredCategoriesArr.forEach((cat) => {
          const categoryLink = createCategoryLink(cat);
          res.push(
            <CategoryLabel
              key={cat?.id}
              name={cat?.title}
              url={categoryLink}
              textMaxWidth={WIDE_CATEGORY_LABEL_WIDTH}
            />,
          );
        });
      }
    }
    if (item?.difficulty) {
      res.push(<DifficultyIcon key="difficulty" difficulty={item?.difficulty} />);
    }
  }

  return res;
};

export const getGeneralizedObject = (
  item,
  videoProgress?: DashVideo | null,
  isGridHit = false,
  locale = 'en',
) => {
  const categories = getCategoriesArray(item, isGridHit);
  switch (item.searchable_type) {
    case HITS_TYPES.VIDEO: {
      const { linkToShow, progressToShow } = calcVideoProgressAndLinkToShow(
        videoProgress?.completed,
        videoProgress?.last_position,
        item?.duration,
        item?.video_url,
        videoProgress?.chapter,
        videoProgress?.time,
      );

      return {
        id: item?.id,
        type: item?.type,
        title: item?.title,
        description: item?.description,
        preview_url: item?.preview_url,
        user: item?.user,
        userLink: item?.user_slug && `${DEFAULT_DISCOVER_URL}/experts/${item?.user_slug}`,
        duration: secondsToHms(item?.duration),
        searchableType: item.searchable_type,
        categoriesArr: categories,
        link: linkToShow,
        progress: progressToShow,
        isReactLink: true,
      };
    }
    case HITS_TYPES.PATHWAY: {
      return {
        id: item?.id,
        type: item?.type,
        title: item.name,
        preview_url: item?.image_url,
        description: item.description,
        count_videos: item?.count_videos,
        accompanyingText:
          `${item?.count_videos} ${item?.count_videos > 1 ? 'videos' : 'video'}` +
          ' • ' +
          secondsToHHHmmm(item?.duration),
        searchableType: item.searchable_type,
        categoriesArr: categories,
        categories: item?.categories_array,
        link: `${DEFAULT_DISCOVER_URL}/pathways/${item.slug}`,
        isReactLink: true,
        slug: item.slug,
        cpdCredits: item?.cpd_credits,
      };
    }
    case HITS_TYPES.NEWS: {
      return {
        id: item?.id,
        type: item?.type,
        title: item.title,
        preview_url: item?.image_url,
        description: item.post_introduction,
        accompanyingText:
          formatDateToNewsFormat({ dateString: item?.published_at, locale }) + ' • ' + item?.user,
        searchableType: item.searchable_type,
        categoriesArr: categories,
        link: `${DEFAULT_DISCOVER_URL}${DiscoverRoutes.Insights}/${item.slug || item.id}`,
        isReactLink: true,
      };
    }
    default:
      return null;
  }
};

interface HitProps {
  hit: any;
  pathwayProgress?: DiscoverPathwaysDetailedInfo | null;
  videoProgress?: DashVideo | null;
  tenantTitle?: string | null;
  tenantName?: string | null;
  isInWatchlist?: boolean;
  sendEvent: SendHitsEvent;
}

const Hit: FC<HitProps> = ({
  hit,
  pathwayProgress,
  videoProgress,
  tenantTitle,
  tenantName,
  isInWatchlist = false,
  sendEvent,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const data = getGeneralizedObject(hit, videoProgress, false, i18n.language);

  const { userInfo } = useContext(UserInfoCtx);
  const [toggleFromWatchlist, toggleFromWatchlistLoading] = useToggleFromWatchlist(
    isInWatchlist,
    data?.id,
  );

  const handleClickHit = () => {
    sendAlgoliaClickEvent(sendEvent, hit);
  };

  if (hit.searchable_type === HITS_TYPES.GLOSSARY) {
    return (
      <GlossaryHit
        slug={hit?.slug}
        title={hit?.title}
        description={hit?.definition}
        categories={hit?.categories_array}
        videoUrl={hit?.video_url}
        handleClickHit={handleClickHit}
      />
    );
  }

  const hasPathwayProgress =
    data?.searchableType === HITS_TYPES.PATHWAY &&
    !!pathwayProgress?.count_watched_videos &&
    !!data?.count_videos;
  const hash =
    pathwayProgress?.passed_quizes && pathwayProgress.passed_quizes[0]
      ? pathwayProgress.passed_quizes[0]?.hash
      : undefined;
  const passDate =
    pathwayProgress?.passed_quizes && pathwayProgress.passed_quizes[0]
      ? pathwayProgress.passed_quizes[0]?.date_passing
      : undefined;
  const testId =
    pathwayProgress?.not_passed_quizes && pathwayProgress.not_passed_quizes[0]
      ? pathwayProgress.not_passed_quizes[0]?.id
      : undefined;
  const hasUncompletedQuizes = Boolean(pathwayProgress?.not_passed_quizes?.length);

  return (
    <>
      <StyledCard noMb={hasPathwayProgress}>
        <StyledRatio ratio={68}>
          <StyledLink to={data?.link} onClick={handleClickHit}>
            <CardImg
              wrapperCss={videoProgress?.completed ? cardImgCompletedWrapperCss : cardImgWrapperCss}
              imgPath={data?.preview_url}
            >
              {data?.searchableType === HITS_TYPES.VIDEO && (
                <>
                  {videoProgress?.completed ? <CardRepeatIcon /> : <CardPlayIcon />}
                  {videoProgress?.completed && <CardLabel>{t('page.pathway.completed')}</CardLabel>}
                  {data?.progress && <ProgressLine width={data.progress} />}
                </>
              )}
              {data?.searchableType === HITS_TYPES.PATHWAY && (
                <>
                  <CardGragient />
                  <CardImageLabel
                    name={t('page.discover.pathway')}
                    bgColor={theme.palette.common.pink}
                    textColor={theme.palette.secondary.dark}
                  />
                  {pathwayProgress?.count_watched_videos && data?.count_videos && (
                    <>
                      {hash || !testId ? (
                        <CardLabel>
                          {hasUncompletedQuizes
                            ? t('page.pathway.watched')
                            : t('page.pathway.completed')}
                        </CardLabel>
                      ) : (
                        <CardProgressLabel
                          current={pathwayProgress.count_watched_videos}
                          total={data.count_videos}
                        />
                      )}
                      <ProgressLine
                        width={(pathwayProgress.count_watched_videos * 100) / data.count_videos}
                      />
                    </>
                  )}
                </>
              )}
              {data?.searchableType === HITS_TYPES.NEWS && (
                <CardImageLabel
                  name={t('page.discover.news')}
                  bgColor={theme.palette.common.blueLight}
                />
              )}
            </CardImg>
          </StyledLink>
        </StyledRatio>
        <CardInfo>
          <LabelsWrapper>
            {data?.categoriesArr && data?.categoriesArr.length > 0 && (
              <CardLabels wrapperCss={cardLabelsWrapperCss}>{data.categoriesArr}</CardLabels>
            )}
            {!!data?.cpdCredits && (
              <StyledCategoryLabelWrapper bgColor={theme.palette.common.violetAnalyticsCard}>
                <CpdScore>{data.cpdCredits}</CpdScore>
                <CpdTitle>{t('common.cpdCredits')}</CpdTitle>
              </StyledCategoryLabelWrapper>
            )}
          </LabelsWrapper>
          {data?.title && (
            <TitleWrapper to={data?.link} onClick={handleClickHit}>
              <CardHeader text={data.title} />
            </TitleWrapper>
          )}
          {data?.description && <CardDescription text={data.description} />}
          <InfoRow>
            {data?.accompanyingText ? (
              <CardAccompanyingInfo text={data.accompanyingText} />
            ) : data?.user || data?.duration ? (
              <CardDurationInfo
                durationName={data?.user}
                duration={data?.duration}
                link={data?.userLink}
                reverse={data?.searchableType === HITS_TYPES.NEWS}
              />
            ) : null}
            {data?.searchableType === HITS_TYPES.VIDEO && userInfo && (
              <ToggleFromList
                disabled={toggleFromWatchlistLoading}
                isAdd={!isInWatchlist}
                onClick={toggleFromWatchlist}
              />
            )}
          </InfoRow>
        </CardInfo>
      </StyledCard>
      {data?.searchableType === HITS_TYPES.PATHWAY &&
        !!pathwayProgress?.count_watched_videos &&
        !!data?.count_videos && (
          <CardPathwayActionBlock
            category={data?.categories?.[0]?.title}
            wrapperCss={cardPathwayActionBlockWrapperCss}
            pathwaySlug={data?.slug}
            totalVideos={data.count_videos}
            watchedVideos={pathwayProgress.count_watched_videos}
            hash={hash}
            testId={testId}
            passDate={passDate}
            tenantTitle={tenantTitle}
            tenantName={tenantName}
            pathwayName={data?.title}
            cpdCredits={data?.cpdCredits}
          />
        )}
    </>
  );
};

export default Hit;

const cardImgWrapperCss = css`
  height: 100%;
  margin-bottom: 0;
`;

const cardImgCompletedWrapperCss = ({ theme }) => css`
  height: 100%;
  margin-bottom: 0;

  &::after {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: ${alpha(theme.palette.common.blueDark, 0.66)};
  }
`;
const cardLabelsWrapperCss = css`
  &:not(:only-child) {
    margin-right: 10px;
  }
`;
const cardPathwayActionBlockWrapperCss = ({ theme }) => css`
  margin-bottom: ${MIDDLE_MARGIN_PX};

  ${theme.breakpoints.up('md')} {
    margin-bottom: 0;
  }
`;
const StyledCategoryLabelWrapper = styled(CategoryLabelWrapper)`
  display: flex;
  align-items: center;
  margin-bottom: ${SMALL_MARGIN_PX};
`;

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'noMb',
})`
  margin-bottom: ${({ noMb }) => noMb && 0};
`;
const StyledRatio = styled(Ratio)`
  flex: 1;
  margin-bottom: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: 0;
  }
`;
const StyledLink = styled(Link)`
  display: block;
  height: 100%;
`;
const LabelsWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;

  & .cpdLabel {
    padding: 4.5px 9px;
  }
`;
const CpdScore = styled('div')`
  font-weight: bold;
  color: ${({ theme }) => theme.palette.common.white};
  font-size: 20px;
  line-height: 1;

  ${({ theme }) => theme.breakpoints.up('xl')} {
    margin-right: 9px;
  }
`;
const CpdTitle = styled('div')`
  display: none;
  color: ${({ theme }) => theme.palette.common.white};
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;

  ${({ theme }) => theme.breakpoints.up('xl')} {
    display: block;
  }
`;
const TitleWrapper = styled(Link)`
  color: ${({ theme }) => theme.palette.common.white};
`;
const InfoRow = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
